import { useCallback } from "react"
import { useMutation } from "urql"

import type { CreateCommentMutationMutation, CreateCommentMutationMutationVariables } from "@/graphql/codegen/graphql"
import {
  CommentParentTableType,
  CreateCommentMutationDocument,
  FieldGroupEnum,
  ReferenceTableName,
} from "@/graphql/codegen/graphql"
import { useFileUpload } from "@/hooks/useFileUpload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateAdjustmentAttachmentsReturn {
  createAdjustmentAttachments: (adjustmentId: string, comment?: string, document?: File) => Promise<boolean>
  isCreating: boolean
}

export const useCreateAdjustmentAttachments = (): UseCreateAdjustmentAttachmentsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { uploadFile } = useFileUpload()

  const [{ fetching: isCreatingComment }, executeCreateComment] = useMutation<
    CreateCommentMutationMutation,
    CreateCommentMutationMutationVariables
  >(CreateCommentMutationDocument)

  const createAdjustmentAttachments = useCallback(
    async (adjustmentId: string, comment?: string, document?: File): Promise<boolean> => {
      if (comment?.trim()) {
        const { error: commentError } = await executeCreateComment({
          input: {
            value: comment,
            referenceId: adjustmentId,
            type: CommentParentTableType.InventoryAdjustment,
            isActive: true,
          },
        })

        if (commentError) {
          notify({ type: NotificationType.Error, message: commentError.message })
          return false
        }
      }

      if (document) {
        const uploadSuccess = await uploadFile({
          file: document,
          type: FieldGroupEnum.Miscellaneous,
          metadata: {
            referenceId: adjustmentId,
            referenceTableName: ReferenceTableName.InventoryAdjustment,
            fieldGroup: FieldGroupEnum.Miscellaneous,
          },
        })

        if (!uploadSuccess) {
          notify({
            type: NotificationType.Error,
            message: "Inventory adjustment created but failed to upload document.",
          })
          return false
        }
      }

      return true
    },
    [executeCreateComment, uploadFile, notify]
  )

  return { createAdjustmentAttachments, isCreating: isCreatingComment }
}
